/**
 * Joystick Instance
 * Represents a single joystick UI element with its own methods
 */

import type { JoystickUIConfig, IJoystick } from '../types';

export class JoystickInstance implements IJoystick {
    readonly id: string;
    private container: HTMLElement;
    private knobElement: HTMLElement;
    private config: Required<JoystickUIConfig>;

    constructor(config: JoystickUIConfig) {
        this.id = config.id;
        this.config = this.applyDefaults(config);
        const { container, knobElement } = this.createElements();
        this.container = container;
        this.knobElement = knobElement;
    }

    private applyDefaults(config: JoystickUIConfig): Required<JoystickUIConfig> {
        return {
            type: config.type,
            id: config.id,
            position: config.position ?? 'bottom-left',
            customPosition: config.customPosition ?? { x: 0.15, y: 0.85 },
            positionMode: config.positionMode ?? 'dynamic',
            visibilityMode: config.visibilityMode ?? 'on-touch',
            positionMargin: config.positionMargin ?? 80,
            size: config.size ?? 140,
            knobSize: config.knobSize ?? 60,
            baseColor: config.baseColor ?? 'rgba(255, 255, 255, 0.3)',
            knobColor: config.knobColor ?? 'rgba(255, 255, 255, 0.7)',
            activeColor: config.activeColor ?? 'rgba(100, 200, 255, 0.8)',
            opacity: config.opacity ?? 0.8,
            zIndex: config.zIndex ?? 1000,
            showOnDesktop: config.showOnDesktop ?? false,
            container: config.container ?? document.body,
        };
    }

    private createElements(): { container: HTMLElement; knobElement: HTMLElement } {
        const { size, knobSize, baseColor, knobColor, opacity, zIndex, visibilityMode, showOnDesktop } = this.config;

        // Create container
        const container = document.createElement('div');
        container.id = `joystick-${this.id}`;
        container.className = 'plutus-joystick-container';

        const initialOpacity = visibilityMode === 'always' ? opacity : 0;

        container.style.cssText = `
            position: fixed;
            width: ${size}px;
            height: ${size}px;
            opacity: ${initialOpacity};
            transition: opacity 0.2s ease;
            z-index: ${zIndex};
            pointer-events: none;
            display: ${showOnDesktop ? 'block' : 'none'};
        `;

        // Show on mobile/touch devices
        if (!showOnDesktop && this.isTouchDevice()) {
            container.style.display = 'block';
        }

        // Set initial position
        this.setContainerPosition(container);

        // Create base (outer circle)
        const baseElement = document.createElement('div');
        baseElement.className = 'plutus-joystick-base';
        baseElement.style.cssText = `
            position: absolute;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            background: ${baseColor};
            border: 2px solid rgba(255, 255, 255, 0.2);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        `;

        // Create knob (inner circle)
        const knobElement = document.createElement('div');
        knobElement.className = 'plutus-joystick-knob';
        const knobOffset = (size - knobSize) / 2;
        knobElement.style.cssText = `
            position: absolute;
            width: ${knobSize}px;
            height: ${knobSize}px;
            border-radius: 50%;
            background: ${knobColor};
            border: 2px solid rgba(255, 255, 255, 0.4);
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
            left: ${knobOffset}px;
            top: ${knobOffset}px;
            transition: transform 0.1s ease, background-color 0.2s ease;
        `;

        container.appendChild(baseElement);
        container.appendChild(knobElement);
        this.config.container.appendChild(container);

        return { container, knobElement };
    }

    private setContainerPosition(container: HTMLElement): void {
        const { position, customPosition, positionMargin } = this.config;

        let x = 0, y = 0;

        switch (position) {
            case 'bottom-left':
                x = positionMargin;
                y = window.innerHeight - positionMargin;
                break;
            case 'bottom-right':
                x = window.innerWidth - positionMargin;
                y = window.innerHeight - positionMargin;
                break;
            case 'top-left':
                x = positionMargin;
                y = positionMargin;
                break;
            case 'top-right':
                x = window.innerWidth - positionMargin;
                y = positionMargin;
                break;
            case 'custom':
                x = window.innerWidth * customPosition.x;
                y = window.innerHeight * customPosition.y;
                break;
        }

        container.style.left = `${x}px`;
        container.style.top = `${y}px`;
        container.style.transform = 'translate(-50%, -50%)';
    }

    update(vector: { x: number; y: number }, isActive: boolean, centerPosition?: { x: number; y: number }): void {

        // Update position for dynamic mode
        if (this.config.positionMode === 'dynamic' && centerPosition) {
            this.container.style.left = `${centerPosition.x}px`;
            this.container.style.top = `${centerPosition.y}px`;
        }

        // Update visibility
        if (this.config.visibilityMode === 'on-touch') {
            this.container.style.opacity = isActive ? String(this.config.opacity) : '0';
        }

        // Update knob position
        const maxDistance = (this.config.size - this.config.knobSize) / 2;
        const knobX = vector.x * maxDistance;
        const knobY = -vector.y * maxDistance; // Invert Y for screen coordinates

        this.knobElement.style.transform = `translate(${knobX}px, ${knobY}px)`;

        // Update colors when active
        if (isActive) {
            this.knobElement.style.backgroundColor = this.config.activeColor;
        } else {
            this.knobElement.style.backgroundColor = this.config.knobColor;
        }
    }

    setVisible(visible: boolean): void {
        this.container.style.display = visible ? 'block' : 'none';
    }

    remove(): void {
        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }

    getElement(): HTMLElement {
        return this.container;
    }

    private isTouchDevice(): boolean {
        return (
            'ontouchstart' in window ||
            navigator.maxTouchPoints > 0 ||
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        );
    }
}

